/** @jsxImportSource @emotion/react */
import React, { useState, useCallback, useMemo } from 'react'
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import { ChevronDown, ChevronRight } from './icons'
import { formatGamesPerTeam } from '../utils/transformGames'

// ─── Data Interfaces ─────────────────────────────────────────────

export interface TeamRow {
  id: string
  name: string
  games: number
  home: number
  visitor: number
  avgStartTime: string
  lateStartTime: string
  exceptions: number
}

export type DivisionScheduleStatus =
  | 'pending'
  | 'draft'
  | 'approved'
  | 'applied'

export interface DivisionRow {
  id: string
  name: string
  games: number
  /** Exact mean games per team (may be fractional) — rounded at render. */
  gamesPerTeam: number
  avgStartTime: string
  players: number
  teams: number
  divisionScore: string
  status?: DivisionScheduleStatus
  teamRows: TeamRow[]
}

// ─── Status Badge ────────────────────────────────────────────────

const ReviewStatusBadge: React.FC<{ status?: DivisionScheduleStatus }> = ({
  status,
}) => {
  let bgColor: string
  let textColor: string
  let borderColor: string
  let label: string

  switch (status) {
    case 'applied':
    case 'approved':
      bgColor = `${colors.SUCCESS_300}33`
      textColor = colors.SUCCESS_300
      borderColor = `${colors.SUCCESS_300}60`
      label = 'Published'
      break
    case 'draft':
      bgColor = colors.NEUTRAL_700
      textColor = colors.NEUTRAL_50
      borderColor = colors.NEUTRAL_100
      label = 'Draft'
      break
    case 'pending':
    default:
      bgColor = `${colors.WARNING_300}20`
      textColor = colors.WARNING_300
      borderColor = `${colors.WARNING_300}60`
      label = 'Pending'
      break
  }

  return (
    <span
      css={css`
        display: inline-block;
        padding: 2px 4px;
        border-radius: 4px;
        background: ${bgColor};
        color: ${textColor};
        border: 1px solid ${borderColor};
        font-family: 'Inter', sans-serif;
        font-size: 12px;
        font-weight: 400;
        white-space: nowrap;
        text-align: center;
      `}
    >
      {label}
    </span>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const containerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 16px;
`

/* Column header row */
const headerRowStyles = css`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 5px 16px;
  background-color: ${colors.SECONDARY_400};
  border-radius: 8px;
`

const headerCellStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 17.4px;
  color: ${colors.NEUTRAL_70};
  text-transform: uppercase;
`

const nameColStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 220px;
  min-width: 220px;
  flex-shrink: 0;
`

const flexColStyles = css`
  flex: 1;
  min-width: 0;
  min-height: 1px;
`

/* Data row shared */
const dataRowStyles = css`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background-color: ${colors.SECONDARY_400};
  border-radius: 8px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: #1e2733;
  }
`

const cellTextStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20.3px;
  color: ${colors.WHITE};
`

const scoreStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20.3px;
  color: ${colors.SUCCESS_200};
`

/* Conference summary row */
const conferenceRowStyles = css`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background-color: ${colors.SECONDARY_300};
  border-radius: 8px;
`

const conferenceCellStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20.3px;
  color: ${colors.NEUTRAL_50};
`

/* Team header row */
const teamHeaderRowStyles = css`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 16px 4px 32px;
  background-color: ${colors.SECONDARY_500};
`

/* Team data row */
const teamDataRowStyles = css`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px 8px 32px;
  background-color: ${colors.SECONDARY_500};
`

const teamHeaderCellStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 17.4px;
  color: ${colors.NEUTRAL_70};
  text-transform: uppercase;
`

const teamNameColStyles = css`
  width: 204px;
  min-width: 204px;
  flex-shrink: 0;
`

const teamFlexColStyles = css`
  flex: 1;
  min-width: 0;
  min-height: 1px;
`

const teamDataCellStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20.3px;
  color: ${colors.NEUTRAL_30};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

// ─── Component ───────────────────────────────────────────────────

interface ScheduleReviewTableProps {
  divisions: DivisionRow[]
  loading?: boolean
  showConference?: boolean
  onRevertByDivisions?: (divisionUids: string[]) => void
}

const ScheduleReviewTable: React.FC<ScheduleReviewTableProps> = ({
  divisions,
  loading = false,
  showConference = false,
}) => {
  const [expandedDivisions, setExpandedDivisions] = useState<Set<string>>(
    new Set()
  )
  // Conference aggregated stats
  const conferenceStats = useMemo(() => {
    if (!showConference || divisions.length === 0) {
      return null
    }
    const totalGames = divisions.reduce((sum, d) => sum + d.games, 0)
    const totalTeams = divisions.reduce((sum, d) => sum + d.teams, 0)
    const totalPlayers = divisions.reduce((sum, d) => sum + d.players, 0)

    // WEIGHTED by team count — the mean over every team in the flight, not the
    // old mean-of-division-means, which weighted a 5-team division the same as a
    // 6-team one. (233 games over 16 teams is 29.1, not the 29.2 that averaging
    // 27.7/30/30 produced.) Rounded at render like the division rows.
    const appearanceSum = divisions.reduce(
      (sum, d) => sum + d.gamesPerTeam * d.teams,
      0
    )
    const gamesPerTeam = totalTeams > 0 ? appearanceSum / totalTeams : 0

    return {
      totalGames,
      totalTeams,
      totalPlayers,
      gamesPerTeam,
    }
  }, [showConference, divisions])

  const toggleDivision = useCallback((id: string) => {
    setExpandedDivisions((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const isApprovedOrApplied = (status?: DivisionScheduleStatus) =>
    status === 'applied' || status === 'approved'

  return (
    <div css={containerStyles}>
      {/* Column headers */}
      <div css={headerRowStyles}>
        <div css={nameColStyles}>
          {/* Chevron placeholder */}
          <div
            css={css`
              width: 24px;
              height: 8px;
              flex-shrink: 0;
              opacity: 0;
            `}
          />
          <span css={headerCellStyles}>DIV NAME</span>
        </div>
        <div css={flexColStyles}>
          <span css={headerCellStyles}>GAMES</span>
        </div>
        <div css={flexColStyles}>
          <span css={headerCellStyles}>GAMES/TEAM</span>
        </div>
        <div css={flexColStyles}>
          <span css={headerCellStyles}>PLAYERS</span>
        </div>
        <div css={flexColStyles}>
          <span css={headerCellStyles}>TEAMS</span>
        </div>
        <div css={flexColStyles}>
          <span css={headerCellStyles}>DIVISION SCORE</span>
        </div>
        <div css={flexColStyles}>
          <span css={headerCellStyles}>SCHEDULE</span>
        </div>
      </div>

      {/* Empty / loading state */}
      {divisions.length === 0 && !loading && (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 32px 16px;
            background-color: ${colors.SECONDARY_400};
            border-radius: 8px;
            font-family: 'Inter', sans-serif;
            font-size: 14px;
            color: ${colors.NEUTRAL_50};
          `}
        >
          No schedule data yet. Generate a schedule to see division results
          here.
        </div>
      )}

      {/* Conference summary row */}
      {conferenceStats && (
        <div css={conferenceRowStyles}>
          <div css={nameColStyles}>
            <div
              css={css`
                width: 24px;
                height: 24px;
                flex-shrink: 0;
              `}
            />
          </div>
          <div css={flexColStyles}>
            <span css={conferenceCellStyles}>{conferenceStats.totalGames}</span>
          </div>
          <div css={flexColStyles}>
            <span css={conferenceCellStyles}>
              {formatGamesPerTeam(conferenceStats.gamesPerTeam)}
            </span>
          </div>
          <div css={flexColStyles}>
            <span css={conferenceCellStyles}>
              {conferenceStats.totalPlayers > 0
                ? `${conferenceStats.totalPlayers} Players`
                : '--'}
            </span>
          </div>
          <div css={flexColStyles}>
            <span css={conferenceCellStyles}>
              {conferenceStats.totalTeams} Teams
            </span>
          </div>
          <div css={flexColStyles}>
            <span css={conferenceCellStyles}>--</span>
          </div>
          <div css={flexColStyles} />
        </div>
      )}

      {/* Division data rows */}
      {divisions.map((div) => {
        const divExpanded = expandedDivisions.has(div.id)
        const approved = isApprovedOrApplied(div.status)

        return (
          <React.Fragment key={div.id}>
            <div
              css={[
                dataRowStyles,
                approved &&
                  css`
                    background-color: ${colors.SUCCESS_300}15;
                    &:hover {
                      background-color: ${colors.SUCCESS_300}22;
                    }
                  `,
              ]}
              onClick={() => toggleDivision(div.id)}
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  toggleDivision(div.id)
                }
              }}
            >
              <div css={nameColStyles}>
                <div
                  css={css`
                    width: 24px;
                    height: 24px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                  `}
                >
                  {divExpanded ? (
                    <ChevronDown size={24} />
                  ) : (
                    <ChevronRight size={24} />
                  )}
                </div>
                <span
                  css={[
                    cellTextStyles,
                    css`
                      overflow: hidden;
                      text-overflow: ellipsis;
                      white-space: nowrap;
                    `,
                  ]}
                >
                  {div.name}
                </span>
              </div>
              <div css={flexColStyles}>
                <span css={cellTextStyles}>{div.games}</span>
              </div>
              <div css={flexColStyles}>
                <span css={cellTextStyles}>
                  {formatGamesPerTeam(div.gamesPerTeam)}
                </span>
              </div>
              <div css={flexColStyles}>
                <span css={cellTextStyles}>
                  {div.players > 0 ? `${div.players} Players` : '--'}
                </span>
              </div>
              <div css={flexColStyles}>
                <span css={cellTextStyles}>{div.teams} Teams</span>
              </div>
              <div css={flexColStyles}>
                <span css={scoreStyles}>{div.divisionScore}</span>
              </div>
              <div css={flexColStyles}>
                <ReviewStatusBadge status={div.status} />
              </div>
            </div>

            {/* Team header + data rows */}
            {divExpanded && div.teamRows.length > 0 && (
              <div>
                {/* Team column headers */}
                <div
                  css={[
                    teamHeaderRowStyles,
                    approved &&
                      css`
                        background-color: ${colors.SUCCESS_300}10;
                      `,
                  ]}
                >
                  <div css={[teamHeaderCellStyles, teamNameColStyles]}>
                    TEAM
                  </div>
                  <div css={[teamHeaderCellStyles, teamFlexColStyles]}>
                    GAMES
                  </div>
                  <div css={[teamHeaderCellStyles, teamFlexColStyles]}>
                    HOME
                  </div>
                  <div css={[teamHeaderCellStyles, teamFlexColStyles]}>
                    VISITOR
                  </div>
                  <div css={[teamHeaderCellStyles, teamFlexColStyles]}>
                    AVG START TIME
                  </div>
                  <div css={[teamHeaderCellStyles, teamFlexColStyles]}>
                    LATE START TIME
                  </div>
                  <div css={teamFlexColStyles} />
                </div>

                {/* Team data rows */}
                {div.teamRows.map((team) => (
                  <div
                    key={team.id}
                    css={[
                      teamDataRowStyles,
                      approved &&
                        css`
                          background-color: ${colors.SUCCESS_300}12;
                        `,
                    ]}
                  >
                    <div css={[teamDataCellStyles, teamNameColStyles]}>
                      {team.name}
                    </div>
                    <div css={[teamDataCellStyles, teamFlexColStyles]}>
                      {team.games}
                    </div>
                    <div css={[teamDataCellStyles, teamFlexColStyles]}>
                      {team.home}
                    </div>
                    <div css={[teamDataCellStyles, teamFlexColStyles]}>
                      {team.visitor}
                    </div>
                    <div css={[teamDataCellStyles, teamFlexColStyles]}>
                      {team.avgStartTime}
                    </div>
                    <div css={[teamDataCellStyles, teamFlexColStyles]}>
                      {team.lateStartTime}
                    </div>
                    <div css={teamFlexColStyles} />
                  </div>
                ))}
              </div>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

export default ScheduleReviewTable
